import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import {
  fetchPracticeSummary,
  fetchPractice,
  practiceContextChanged
} from '../../../store/slices/practiceSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserMd,
  faUsers,
  faUserNurse,
  faUserInjured,
  faCalendarAlt,
  faFileImport,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import PracticeDetails from '../PracticeDetails/PracticeDetails';
import AdminManagement from '../AdminManagement/AdminManagement';
import ProviderManagement from '../ProviderManagement/ProviderManagement';
import CaregiverManagement from '../CaregiverManagement/CaregiverManagement';
import PatientManagement from '../PatientManagement/PatientManagement';
import SystemCaregiverManagement from '../SystemCaregiverManagement/SystemCaregiverManagement';
import ScheduleTargetSettings from '../ScheduleTargetSettings/ScheduleTargetSettings';
import ImportPatients from '../ImportPatients/ImportPatients';
import './PracticeDashboard.css';
import { resolvePracticeLogoUrl } from '../../../utils/assetUrls';

/**
 * Local ErrorBoundary to avoid adding external deps.
 * Shows a visible fallback instead of leaving a blank area when a child throws.
 */
class LocalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // keep logging so devs can see stack traces in console
    console.error('[LocalErrorBoundary] caught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
    // optionally call parent's onReset if provided
    if (typeof this.props.onReset === 'function') {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.error) {
      const Fallback = this.props.FallbackComponent;
      // render user-provided fallback or minimal fallback
      if (Fallback) {
        return <Fallback error={this.state.error} resetErrorBoundary={this.reset} />;
      }
      return (
        <div style={{ padding: 12, border: '1px solid #e74c3c', background: '#fff5f5', borderRadius: 6 }}>
          <strong>Something went wrong.</strong>
          <div style={{ marginTop: 8, fontSize: 13 }}>{this.state.error?.message || 'Unknown error'}</div>
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-sm btn-outline-secondary" onClick={this.reset}>Retry</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Visible fallback UI for schedule tab errors */
const ScheduleErrorFallback = ({ error, resetErrorBoundary }) => {
  return (
    <div style={{
      border: '1px solid #e74c3c',
      background: '#fff5f5',
      color: '#a94442',
      padding: 12,
      borderRadius: 6,
      margin: 8
    }}>
      <strong>Schedule UI failed to render.</strong>
      <div style={{ marginTop: 6, fontSize: 13 }}>
        {error?.message || 'Unknown render error in ScheduleTargetSettings'}
      </div>
      <div style={{ marginTop: 8 }}>
        <button className="btn btn-sm btn-outline-secondary" onClick={resetErrorBoundary}>
          Retry
        </button>
      </div>
    </div>
  );
};

const isPracticeLockedStatus = (status) => {
  if (status == null) return false;
  if (typeof status === 'number') return status === 4;
  const normalized = String(status).trim().toLowerCase();
  return normalized === '4' || normalized === 'locked';
};

const PracticeDashboard = () => {
  const { practiceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth); // Get user for role check
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const dispatch = useDispatch();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  // Practice data from Redux store
  const [practiceData, setPracticeData] = useState(() => {
    if (!currentPractice) return null;
    return String(currentPractice.id) === String(practiceId) ? currentPractice : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!currentPractice) return true;
    return String(currentPractice.id) !== String(practiceId);
  });
  const tabContentRef = useRef(null);

  // All tabs (including previously in More dropdown)
  const tabs = useMemo(() => ([
    { id: 'practice', label: 'Practice', icon: faUserMd },
    { id: 'admin', label: 'Admin', icon: faUsers },
    { id: 'provider', label: 'Provider', icon: faUserMd },
    { id: 'caregiver', label: 'Caregiver', icon: faUserNurse },
    { id: 'patient', label: 'Patient', icon: faUserInjured },
    // System Caregiver disabled
    // { id: 'system-caregiver', label: 'System Caregiver', icon: faUserCheck },
    { id: 'schedule', label: 'Schedule & Target setting', icon: faCalendarAlt },
    { id: 'import', label: 'Import Patients', icon: faFileImport }
  ]), []);

  const visibleTabs = useMemo(() => {
    const roleId = Number(user?.role_id);
    // Practice Caregiver should not see admin/provider/system caregiver/schedule/import tabs.
    if (roleId === 5) {
      return tabs.filter((t) => ['practice', 'patient'].includes(t.id));
    }
    // System caregiver view already hides the tab bar entirely (handled below).
    return tabs;
  }, [tabs, user?.role_id]);

  const getTabFromSearch = useCallback((search) => {
    // If System Caregiver (Role 7), always force 'patient' tab logic conceptually,
    // though we might want to allow them to stay there if we were showing tabs.
    // But since we are hiding tabs, we want to start at 'patient'.
    if (user?.role_id === 7) return 'patient';

    const params = new URLSearchParams(search);
    const requested = params.get('tab');
    // Restrict by role (avoid showing unauthorized tabs when navigating back)
    if (Number(user?.role_id) === 5) {
      return ['practice', 'patient'].includes(requested) ? requested : 'patient';
    }
    return tabs.some(tab => tab.id === requested) ? requested : 'practice';
  }, [tabs, user]);


  const [searchParams] = useSearchParams();
  const viewId = searchParams.get('viewId');

  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));

  // If role or route changes make current tab invalid, force it to a visible tab.
  useEffect(() => {
    if (user?.role_id === 7) return;
    if (!visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0]?.id || 'practice');
    }
  }, [activeTab, user?.role_id, visibleTabs]);

  // Entity data for breadcrumb lookup
  const { adminsByPracticeId } = useSelector(state => state.admin ?? {});
  const { providersByPracticeId, caregiversByPracticeId } = useSelector(state => state.practice ?? {});
  const { systemCaregivers } = useSelector(state => state.systemCaregiver ?? {});

  const getEntityName = useCallback((tab, id) => {
    if (!id) return null;
    let list = [];
    const pid = String(practiceId);

    if (tab === 'admin') list = adminsByPracticeId[pid] || [];
    else if (tab === 'provider') list = providersByPracticeId[pid] || [];
    else if (tab === 'caregiver') list = caregiversByPracticeId[pid] || [];
    else if (tab === 'system-caregiver') list = systemCaregivers || [];

    const entity = list.find(e => String(e.id) === String(id));
    if (!entity) return null;

    if (entity.name) return entity.name;
    if (entity.first_name || entity.last_name) {
      return `${entity.first_name || ''} ${entity.last_name || ''}`.trim();
    }
    return null;
  }, [practiceId, adminsByPracticeId, providersByPracticeId, caregiversByPracticeId, systemCaregivers]);

  useEffect(() => {
    const nextTab = getTabFromSearch(location.search);
    setActiveTab(prev => (prev === nextTab ? prev : nextTab));
  }, [location.search, getTabFromSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('tab')) {
      params.set('tab', activeTab || 'practice');
      navigate({
        pathname: location.pathname,
        search: params.toString()
      }, { replace: true });
    }
  }, [location.search, activeTab, location.pathname, navigate]);

  useEffect(() => {
    if (!practiceId) return;
    dispatch(practiceContextChanged(practiceId));
  }, [practiceId, dispatch]);

  useEffect(() => {
    const loadPracticeData = async () => {
      try {
        // Reuse cached practice data if it matches this practiceId.
        if (currentPractice && String(currentPractice.id) === String(practiceId)) {
          setPracticeData(currentPractice);
          setLoading(false);
          return;
        }

        setLoading(true);
        const result = await dispatch(fetchPracticeSummary(practiceId));
        if (fetchPracticeSummary.fulfilled.match(result)) {
          setPracticeData(result.payload.data);
        } else {
          console.error('Failed to load practice:', result.payload);
        }
      } catch (error) {
        console.error('Error loading practice:', error);
      } finally {
        setLoading(false);
      }
    };

    if (practiceId) {
      loadPracticeData();
    }
  }, [practiceId, dispatch, currentPractice]);

  // After edit practice (lock/unlock etc.), refresh summary + Redux practice without full page reload
  useEffect(() => {
    if (!practiceId || !location.state?.practiceUpdated) return;
    let cancelled = false;
    (async () => {
      await dispatch(fetchPractice(practiceId));
      const sum = await dispatch(fetchPracticeSummary(practiceId));
      if (!cancelled && fetchPracticeSummary.fulfilled.match(sum)) {
        setPracticeData(sum.payload.data);
      }
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: {} }
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId, dispatch, navigate, location.pathname, location.search, location.state?.practiceUpdated]);

  // Update breadcrumbs when practice data loads
  useEffect(() => {
    if (practiceData) {
      const isProvider = Number(user?.role_id) === 4;
      const currentTab = tabs.find(tab => tab.id === activeTab);
      const practiceCrumbPath = activeTab === 'practice'
        ? null
        : `${location.pathname}?tab=practice`;

      const baseCrumbs = [];

      if (activeTab === 'practice') {
        if (isProvider) {
          // Provider initial tab: hide breadcrumbs entirely.
          updateBreadcrumbs([]);
          return;
        }
        baseCrumbs.push({ label: 'Practices', path: '/practices', active: false });
        baseCrumbs.push({ label: practiceData.practice_name, path: null, active: true });
      } else {
        if (!isProvider) {
          baseCrumbs.push({ label: 'Practices', path: '/practices', active: false });
        }
        // Provider: show practice name but keep it non-clickable.
        baseCrumbs.push({ label: practiceData.practice_name, path: isProvider ? null : practiceCrumbPath, active: false });

        const entityName = getEntityName(activeTab, viewId);
        if (entityName) {
          const tabPath = `${location.pathname}?tab=${activeTab}`;
          baseCrumbs.push({ label: currentTab?.label || 'Practice', path: tabPath, active: false });
          baseCrumbs.push({ label: entityName, path: null, active: true });
        } else {
          baseCrumbs.push({ label: currentTab?.label || 'Practice', path: null, active: true });
        }
      }

      updateBreadcrumbs(baseCrumbs);
    }
  }, [practiceData, activeTab, tabs, updateBreadcrumbs, location.pathname, viewId, getEntityName, user]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  const practiceLogoUrl = useMemo(() => {
    return resolvePracticeLogoUrl(practiceData?.practice_logo, {
      // Helps bypass stale/bad cached 304 payloads on EC2/proxy deployments.
      cacheBust: practiceData?.updated_at || practiceData?.id || 'practice-logo'
    });
  }, [practiceData?.practice_logo, practiceData?.updated_at, practiceData?.id]);

  const [practiceLogoFailed, setPracticeLogoFailed] = useState(false);
  useEffect(() => {
    setPracticeLogoFailed(false);
  }, [practiceLogoUrl]);

  /** Match legacy stack: MM-DD-YYYY (e.g. 03-01-2026) */
  const formatBillingPeriodDate = (iso) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'N/A';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  const handleTabChange = (tabId) => {
    console.debug('[PracticeDashboard] tab change requested ->', tabId);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    params.delete('viewId'); // Reset to list view when switching tabs
    navigate({
      pathname: location.pathname,
      search: params.toString()
    }, { replace: true });
    setActiveTab(tabId);
  };

  const isPracticeLocked = useMemo(
    () => isPracticeLockedStatus(practiceData?.status),
    [practiceData?.status]
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'practice':
        return <PracticeDetails practiceData={practiceData} />;
      case 'admin':
        return <AdminManagement practiceId={practiceId} isReadOnly={isPracticeLocked} />;
      case 'provider':
        return <ProviderManagement practiceId={practiceId} isReadOnly={isPracticeLocked} />;
      case 'caregiver':
        return <CaregiverManagement practiceId={practiceId} isReadOnly={isPracticeLocked} />;
      case 'patient':
        return <PatientManagement practiceId={practiceId} isReadOnly={isPracticeLocked} />;
      // System Caregiver disabled
      // case 'system-caregiver':
      //   return <SystemCaregiverManagement practiceId={practiceId} isReadOnly={isPracticeLocked} />;
      case 'schedule':
        // Minimal runtime-safety wrapper + force visible block to avoid CSS hiding
        return (
          <div style={{ display: 'block' }}>
            <LocalErrorBoundary FallbackComponent={ScheduleErrorFallback}>
              <ScheduleTargetSettings practiceId={practiceId} isReadOnly={isPracticeLocked} />
            </LocalErrorBoundary>
          </div>
        );
      case 'import':
        return <ImportPatients practiceId={practiceId} isReadOnly={isPracticeLocked} />;
      default:
        return <PracticeDetails practiceData={practiceData} />;
    }
  };

  /* Practice dashboard uses Bootstrap spinner only (no skeleton) for initial load. */
  if (loading) {
    return (
      <div className="practice-dashboard-loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!practiceData) {
    return (
      <div className="practice-dashboard-alert practice-dashboard-alert-danger" role="alert">
        Practice not found!
      </div>
    );
  }

  return (
    <div className="practice-dashboard">
      {/* Practice Header — title left; logo above billing on the right */}
      <div className="practice-dashboard-header">
        <div className="practice-dashboard-header-top">
          <div className="practice-dashboard-header-brand">
            <h2 className="practice-dashboard-title">{practiceData.practice_name}</h2>
            <span className="practice-dashboard-billing-line">
              <span className="practice-dashboard-billing-prefix">Billing Period:</span>{' '}
              <span className="practice-dashboard-billing-dates">
                {formatBillingPeriodDate(practiceData.billing_from_date || practiceData.rpm_start_date)} -{' '}
                {formatBillingPeriodDate(practiceData.billing_to_date || practiceData.rpm_end_date)}
              </span>
            </span>
          </div>

          <div className="practice-dashboard-header-aside">
            {/* System Caregiver button disabled
            {user?.role_id === 7 && (
              <button
                type="button"
                className="btn btn-secondary btn-sm practice-dashboard-back-btn"
                onClick={() => navigate('/system-caregiver-dashboard')}
              >
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Back to Practices
              </button>
            )}
            */}
            {practiceLogoUrl && !practiceLogoFailed && (
              <div className="practice-dashboard-logo-wrap">
                <img
                  src={practiceLogoUrl}
                  alt={`${practiceData.practice_name || 'Practice'} logo`}
                  className="practice-dashboard-logo-img"
                  onError={() => setPracticeLogoFailed(true)}
                />
              </div>
            )}
          </div>
        </div>

        {isPracticeLocked && (
          <div className="alert alert-warning mt-2 mb-0" role="alert">
            This practice is locked. User lists are available in view-only mode.
          </div>
        )}

        {user?.role_id !== 7 && (
          <div className="practice-dashboard-nav-wrap">
            <ul className="practice-dashboard-nav-tabs" role="tablist">
              {visibleTabs.map((tab) => (
                <li key={tab.id} className="practice-dashboard-nav-item" role="presentation">
                  <button
                    className={`practice-dashboard-nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab.id)}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.id}
                  >
                    {/* <FontAwesomeIcon icon={tab.icon} className="practice-dashboard-me-2" /> */}
                    <span className="practice-dashboard-nav-label">{tab.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Tab Content */}
      <div className="practice-dashboard-tab-content" ref={tabContentRef}>
        {renderTabContent()}
      </div>
    </div>
  );
};

export default PracticeDashboard;
